/**
 * SkillState — per-exercise-skill aggregation for the exercise hub and
 * the Coach's Plan.
 *
 * An "exercise skill" maps to one of the 8 free-practice exercise routes
 * (/exercise/scales, /exercise/intervals, …). Each skill spans one or more
 * curriculum nodes. SkillState summarises the user's standing across
 * three pedagogical modes: Listen, Practice, Challenge.
 *
 * listen_sessions and challenge_attempts IndexedDB stores do not exist
 * yet (added in Step 4 of the v2 implementation). Until then, callers
 * pass listenCount=0 and the default ChallengeData, so all listen/
 * challenge fields carry zero/null sentinel values. The computation
 * logic and interface are complete; only the data inputs change.
 */

import type { CurriculumNode, Item } from "./item-library.js";
import { MVP_NODES } from "./item-library.js";
import type { ReviewSource } from "./fsrs.js";
import { MASTERY_PRESETS, type CardStore, type MasteryThresholds } from "./scheduler.js";

// ─── Skill → node mapping ─────────────────────────────────────────────────────

/** URL slug for each free-practice exercise route. */
export type ExerciseSkillId =
  | "scales"
  | "intervals"
  | "chords"
  | "inversions"
  | "cadences"
  | "modes"
  | "progressions"
  | "dictation"
  | "rhythmic-dictation"
  // Guitar track (opt-in) — each its own tile in the guitar hub.
  | "fret-intervals"
  | "fret-scale-degrees"
  | "fret-triads"
  | "fret-inversions"
  | "fret-caged"
  | "fret-caged-anchoring"
  | "fret-scale-degrees-caged"
  | "fret-diatonic"
  | "fret-diatonic-minor"
  | "fret-seventh";

export const ALL_EXERCISE_SKILLS: readonly ExerciseSkillId[] = [
  "scales",
  "intervals",
  "chords",
  "inversions",
  "cadences",
  "modes",
  "progressions",
  "dictation",
  "rhythmic-dictation",
  "fret-intervals",
  "fret-scale-degrees",
  "fret-triads",
  "fret-inversions",
  "fret-caged",
  "fret-caged-anchoring",
  "fret-scale-degrees-caged",
  "fret-diatonic",
  "fret-diatonic-minor",
  "fret-seventh",
];

/** Curriculum node IDs that each exercise skill draws items from. */
export const EXERCISE_SKILL_NODES: Record<ExerciseSkillId, readonly string[]> = {
  scales: [
    "diatonic-degrees-major",
    "diatonic-degrees-minor",
    "chromatic-degrees-major",
  ],
  intervals: [
    "diatonic-intervals-major",
    "chromatic-intervals",
  ],
  chords: [
    "triad-quality",
    "seventh-chord-quality",
    "extended-chords-9",
    "extended-chords-11-13",
    "altered-dominants",
  ],
  inversions: ["triad-inversions"],
  cadences: ["cadence-recognition"],
  modes: [
    "modes-major-family",
    "modes-minor-family",
    "modes-locrian",
  ],
  progressions: [
    "diatonic-progressions",
    "slash-chords",
    "secondary-dominant-recognition",
    "modulation-recognition",
  ],
  dictation: [
    "melodic-dictation-narrow",
    "melodic-dictation-medium",
    "melodic-dictation-chromatic",
  ],
  "rhythmic-dictation": [
    "rhythmic-dictation-simple",
    "rhythmic-dictation-compound",
    "rhythmic-dictation-syncopation",
  ],
  // Guitar track (opt-in) — six rungs, each their own tile in the guitar hub.
  // Rung 1: three shape-family nodes (octave shapes → standard pairs → G–B offset).
  // Rung 2: triad shapes by string set.
  // Rung 3: triad inversions.
  // Rung 4: CAGED shapes (all 5: E/A/G/C/D).
  // Rung 5: CAGED anchoring — shape recognition by dyad, no shape name given.
  // Rung 6: diatonic triads — I/ii/IV/V/vi across all CAGED shapes in key context.
  // Gate all behind `enabledInstrumentTracks`. See docs/design/guitar-harmony-v2.md §4.1.
  "fret-intervals": ["fret-octave-shapes", "fret-wide-octave-shapes", "fret-skip1-intervals", "fret-intervals-standard", "fret-intervals-gb-offset"],
  // Melodic rung: scale degrees on the neck (major) — do…ti relative to a lit tonic.
  "fret-scale-degrees": ["fret-scale-degrees-major", "fret-scale-degrees-minor"],
  "fret-triads": ["fret-triad-shapes"],
  "fret-inversions": ["fret-triad-inversions"],
  "fret-caged": ["fret-caged-shapes-ea", "fret-caged-shapes-cgd"],
  // Rung 5: shape recognition without the shape label — anchor by dyad pattern.
  "fret-caged-anchoring": ["fret-caged-anchoring"],
  // CAGED-linked melodic rung: the tonic shape is lit; fill in re/fa/la/ti inside it.
  "fret-scale-degrees-caged": ["fret-scale-degrees-caged"],
  // Rung 6: diatonic triads in key context — I/ii/IV/V/vi × all 5 CAGED shapes.
  "fret-diatonic": ["fret-diatonic-triads"],
  // Rung 7: minor key diatonic triads — i/♭III/iv/V/♭VII × all 5 CAGED shapes.
  "fret-diatonic-minor": ["fret-diatonic-triads-minor"],
  // Rung 8: 7th chord shapes — maj7/min7/dom7 × all 5 CAGED shapes.
  "fret-seventh": ["fret-seventh-shapes"],
};

/**
 * The exercise setup screen a curriculum node drills on — the single source
 * of truth for node → drill route, derived from `EXERCISE_SKILL_NODES`.
 *
 * Returns web-style paths (`/exercise/<skill>` or `/guitar/<skill>`); mobile
 * translates the guitar slugs (see `apps/mobile/lib/exercise-routes.ts`).
 * Returns `null` when the node has no exercise skill — e.g. a pure-lesson
 * node — so callers render no Drill affordance rather than a dead link.
 *
 * Everything that needs a node's drill route (coach plan, stop builders, the
 * Learn tab on both platforms) goes through here — the private per-surface
 * maps this replaced drifted every time a node was added.
 */
export function nodeExerciseRoute(
  nodeId: string,
): { skillId: ExerciseSkillId; path: string } | null {
  for (const [skill, nodeIds] of Object.entries(EXERCISE_SKILL_NODES)) {
    if ((nodeIds as readonly string[]).includes(nodeId)) {
      const skillId = skill as ExerciseSkillId;
      const prefix = skillId.startsWith("fret-") ? "/guitar" : "/exercise";
      return { skillId, path: `${prefix}/${skillId}` };
    }
  }
  return null;
}

// ─── Input types ─────────────────────────────────────────────────────────────

/**
 * Minimal review record needed for lapse rate computation.
 * Subset of ReviewRecord from the persistence layer — defined here so
 * learning-system stays free of any persistence-package imports.
 */
export interface ReviewSummary {
  itemId: string;
  correct: boolean;
  /** ISO 8601 timestamp, e.g. "2026-05-19T10:30:00.000Z". */
  timestamp: string;
  /** Where this review originated — curriculum practice, a free drill, or a challenge attempt. */
  source?: ReviewSource;
}

/**
 * Challenge data sourced from the challenge_attempts IndexedDB store.
 * Pass the default value until that store is added in Step 4.
 */
export interface ChallengeData {
  /** Best accuracy (0..1) achieved on this skill's Challenge, or null if never attempted. */
  best: number | null;
  attempts: number;
}

const DEFAULT_CHALLENGE_DATA: ChallengeData = { best: null, attempts: 0 };

// ─── SkillState ───────────────────────────────────────────────────────────────

export interface SkillState {
  id: ExerciseSkillId;

  listen: {
    /** Completed Listen sessions for this skill. 0 until listen_sessions store added. */
    count: number;
    /** ISO timestamp of last session, null until listen_sessions store added. */
    lastSession: string | null;
  };

  exercise: {
    /** Items in this skill where card.stability ≥ thresholds.mastered. */
    mastered: number;
    /** Total items belonging to this skill's node set. */
    total: number;
    /** Sum of card.reps across all items in this skill. */
    totalReps: number;
    /** Wrong / total reviews in the last 7 days. 0 when there are no recent reviews. */
    lapse7d: number;
    /** Correct / total over the last 50 reviews in this skill. 0 when no reviews. */
    bestRecent: number;
  };

  challenge: {
    /** "open" once ≥ 4 items in this skill have stability ≥ thresholds.mastered. */
    state: "locked" | "open";
    /** Best accuracy from challenge_attempts. null until store added in Step 4. */
    best: number | null;
    /** Attempt count. 0 until challenge_attempts store added in Step 4. */
    attempts: number;
  };

  /** True when listen.count + exercise.totalReps < 10 (barely any exposure). */
  isNew: boolean;
  /** True when exercise.lapse7d ≥ 0.25 (forgetting > 1 in 4). */
  isLapsing: boolean;
  /** The pedagogical mode this skill recommends, per recommendedMode(). */
  recommendedMode: "listen" | "practice" | "challenge";
}

// ─── recommendedMode ─────────────────────────────────────────────────────────

/**
 * Derive the recommended mode for a skill from its current state.
 *
 * Rules (from drill-modes-design-v2.md §5, drill-hub-design-v2.md §6.4):
 *  1. Newcomer (total exposure < 5) → Listen.
 *  2. Lapsing badly (≥ 30% wrong in last 7 days) → Practice.
 *  3. Mostly mastered (≥ 70%) AND Challenge unlocked → Challenge.
 *  4. Default → Practice.
 */
export function recommendedMode(
  skill: Pick<SkillState, "listen" | "exercise" | "challenge">,
): "listen" | "practice" | "challenge" {
  if (skill.listen.count + skill.exercise.totalReps < 5) return "listen";
  if (skill.exercise.lapse7d >= 0.3) return "practice";
  if (
    skill.exercise.total > 0 &&
    skill.exercise.mastered / skill.exercise.total >= 0.7 &&
    skill.challenge.state === "open"
  )
    return "challenge";
  return "practice";
}

// ─── buildSkillState ─────────────────────────────────────────────────────────

const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;
/** Minimum mastered items required to unlock Challenge mode on a skill. */
const CHALLENGE_UNLOCK_MIN = 4;

/**
 * Build a complete SkillState from available data.
 *
 * @param skillId     - Drill slug (e.g. "modes").
 * @param items       - All curriculum items (function filters by skill).
 * @param cards       - User's FSRS card store.
 * @param thresholds  - Mastery tier thresholds.
 * @param reviews     - All review records for the user (filtered internally by itemId).
 * @param listenCount - Completed Listen sessions; pass 0 until listen_sessions store exists.
 * @param challengeData - Best/attempts from challenge_attempts; pass default until store exists.
 * @param now         - Current time, injectable for testing.
 * @param nodes       - Curriculum node list; defaults to MVP_NODES.
 * @param forceChallengeOpen - Tester override: report Challenge as "open"
 *   regardless of mastery. Driven by the `unlockEverything` pref. Never alters
 *   card state.
 */
export function buildSkillState(
  skillId: ExerciseSkillId,
  items: Item[],
  cards: CardStore,
  thresholds: MasteryThresholds = MASTERY_PRESETS.standard,
  reviews: readonly ReviewSummary[] = [],
  listenCount = 0,
  challengeData: ChallengeData = DEFAULT_CHALLENGE_DATA,
  now: Date = new Date(),
  nodes: CurriculumNode[] = MVP_NODES,
  forceChallengeOpen = false,
): SkillState {
  const nodeIds = new Set(EXERCISE_SKILL_NODES[skillId]);
  const skillItems = items.filter((i) => {
    const node = nodes.find((n) => n.id === i.nodeId);
    return node != null && nodeIds.has(node.id);
  });
  const skillItemIds = new Set(skillItems.map((i) => i.id));

  // Exercise metrics derived from cards
  const total = skillItems.length;
  let mastered = 0;
  let totalReps = 0;
  for (const item of skillItems) {
    const card = cards[item.id];
    if (!card) continue;
    totalReps += card.reps;
    if (card.stability >= thresholds.mastered) mastered++;
  }

  // 7-day lapse rate
  const cutoff = new Date(now.getTime() - SEVEN_DAYS_MS);
  const recent = reviews.filter(
    (r) => skillItemIds.has(r.itemId) && new Date(r.timestamp) >= cutoff,
  );
  const lapse7d =
    recent.length === 0 ? 0 : recent.filter((r) => !r.correct).length / recent.length;

  // Accuracy over last 50 reviews (most recent first)
  const last50 = reviews
    .filter((r) => skillItemIds.has(r.itemId))
    .slice()
    .sort((a, b) => b.timestamp.localeCompare(a.timestamp))
    .slice(0, 50);
  const bestRecent =
    last50.length === 0 ? 0 : last50.filter((r) => r.correct).length / last50.length;

  const challengeState: "locked" | "open" =
    forceChallengeOpen || mastered >= CHALLENGE_UNLOCK_MIN ? "open" : "locked";

  const partial = {
    listen: { count: listenCount, lastSession: null as string | null },
    exercise: { mastered, total, totalReps, lapse7d, bestRecent },
    challenge: {
      state: challengeState,
      best: challengeData.best,
      attempts: challengeData.attempts,
    },
  };

  return {
    id: skillId,
    ...partial,
    isNew: listenCount + totalReps < 10,
    isLapsing: lapse7d >= 0.25,
    recommendedMode: recommendedMode(partial),
  };
}

/**
 * Build SkillState for every drill skill in ALL_EXERCISE_SKILLS order.
 * Reviews are filtered per-skill internally — pass the full review set.
 */
export function buildAllSkillStates(
  items: Item[],
  cards: CardStore,
  thresholds: MasteryThresholds = MASTERY_PRESETS.standard,
  reviews: readonly ReviewSummary[] = [],
  listenCounts: Partial<Record<ExerciseSkillId, number>> = {},
  challengeDataMap: Partial<Record<ExerciseSkillId, ChallengeData>> = {},
  now: Date = new Date(),
  nodes: CurriculumNode[] = MVP_NODES,
  forceChallengeOpen = false,
): SkillState[] {
  return ALL_EXERCISE_SKILLS.map((id) =>
    buildSkillState(
      id,
      items,
      cards,
      thresholds,
      reviews,
      listenCounts[id] ?? 0,
      challengeDataMap[id] ?? DEFAULT_CHALLENGE_DATA,
      now,
      nodes,
      forceChallengeOpen,
    ),
  );
}
